<template>
  <div class="file-upload-container">
    <!-- uploaded view -->
    <div class="uploaded-container" v-show="!editable">
      <div class="uploaded">
        <img
          :src="require('@/assets/images/common/check.svg')"
          class="success"
        />
        <div class="content">
          <p class="upload-placeholder-title">
            {{ uploadDoc.name }}
          </p>
          <div class="file-names-container">
            <p class="file-name" v-for="(file, i) of files" :key="i">
              {{ multiple ? `${i + 1}) ` : '' }}{{ file.name }}
            </p>
          </div>
        </div>
        <!-- edit -->
        <div @click="enableEdit" class="edit" v-if="editAfterUpload">
          <b-icon-pencil-fill />
        </div>
      </div>

      <!-- uploaded list -->
      <div
        class="files-list-container"
        v-if="uploadedFile.file_url && !files.length"
      >
        <template v-for="(file, i) of [uploadedFile]">
          <div class="file-li" :key="i">
            <div class="icon-container">
              <b-icon-file-earmark-text-fill class="file" />
            </div>

            <div class="content">
              <b
                ><p class="file-name">{{ file.file_identifier }}</p></b
              >
            </div>

            <div class="icon-container download">
              <a :href="uploadedFile.file_url" target="_blank"
                ><b-icon-download
              /></a>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- upload view -->
    <div class="uploader" v-show="editable">
      <!-- hidden input-->
      <input
        :multiple="multiple"
        type="file"
        :accept="multiple ? 'application/pdf' : 'image/*,application/pdf'"
        ref="fileSelect"
        class="d-none"
        @change="onFileSelect"
        :disabled="disableUpload"
      />
      <!-- dragging file or select file UI -->
      <drop
        class="file-select-container"
        @dragenter="drag = true"
        @dragover="drag = true"
        @drop="handleDrop"
        @dragleave="drag = false"
      >
        <div @click="openFileSelect" class="file-select" @drop="handleDrop">
          <div class="icon-container">
            <b-icon-file-earmark-text-fill class="file" />
          </div>

          <div class="content text-left">
            <p class="upload-placeholder-title">
              {{ uploadDoc.name }}
            </p>
            <p class="upload-desc">
              {{ $t('invoices.upload.fileUpload.select') }}
            </p>
            <span class="upload-size font-italic">
              {{
                multiple
                  ? `${$t('invoices.upload.fileUpload.formatMsg')}. (${$t(
                      'invoices.upload.fileUpload.size'
                    )})`
                  : $t('invoices.upload.fileUpload.size')
              }}
            </span>
          </div>

          <div class="icon-container">
            <b-icon-paper-clip class="attachment" />
          </div>

          <!-- UI while dragging -->
          <div class="uploader-hover-container" v-show="drag && !disableUpload">
            <!-- can upload -->
            <div
              v-if="filesSizeUnderLimit"
              class="uploader-hover d-flex justify-content-center align-items-center"
            >
              {{ $t('invoices.add.helpers.dropFileHere') }}
            </div>

            <!-- cant upload -->
            <div
              v-else
              class="uploader-hover cannot-upload d-flex justify-content-center align-items-center"
            >
              {{ $t('invoices.add.helpers.fileLimitErr') }}
            </div>
          </div>
        </div>

        <!-- uploaded files list -->
        <div class="files-list-container">
          <div class="file-li" v-for="(file, i) of files" :key="i">
            <div class="icon-container">
              <img
                :src="require('@/assets/images/common/check.svg')"
                class="success"
                v-if="!fileHasErr(file)"
              />
              <img
                :src="require('@/assets/images/common/error-circle.svg')"
                class="success"
                v-else
              />
            </div>

            <div class="content">
              <b
                ><p class="file-name">{{ file.name }}</p></b
              >
              <p v-if="fileHasErr(file)" class="err-msg">
                {{ file.errMsg }}
              </p>
            </div>

            <div v-if="!loading" class="icon-container">
              <button @click="removeFile(i)">
                <img
                  :src="require('@/assets/images/common/cross.svg')"
                  class="cross"
                />
              </button>
            </div>
          </div>
        </div>

        <!-- submit button container -->
        <div class="submit-button-container" v-if="showSubmit && files.length">
          <p class="desc">
            <!-- {{ $t('invoices.add.helpers.uploadMsg') }} -->
          </p>
          <b-button
            :disabled="submitDisabled"
            class="submit-button"
            :class="{ diabled: submitDisabled }"
            @click="onSubmit"
          >
            <loader v-if="loading" :height="30" />
            <span v-else>SUBMIT {{ files.length > 1 ? 'ALL' : '' }}</span>
          </b-button>
        </div>
      </drop>
    </div>
  </div>
</template>

<script>
import uploadService from '@/services/upload'
import { Drop } from 'vue-drag-drop'
import * as Sentry from '@sentry/vue'

const MAX_FILE_SIZE = 52428800 // 50 MB in Bytes(binary)

const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
}

export default {
  props: {
    task: {
      required: true
    },
    uploadDoc: {
      required: true
    },
    multiple: {
      type: Boolean,
      required: false,
      default: false
    },
    uploadedFile: {
      type: Object,
      required: false,
      default: () => ({})
    },
    showSubmit: {
      type: Boolean,
      required: false,
      default: true
    },
    editAfterUpload: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  components: {
    Drop,
    Loader: () => import('@/components/common/Loader')
  },
  mounted() {
    if (this.uploadedFile.file_url) {
      this.editable = false
    }
  },
  data() {
    return {
      files: [],
      drag: false,
      editable: true,
      loading: false
    }
  },
  computed: {
    submitDisabled() {
      return this.loading || Boolean(this.files.find((file) => file.errMsg))
    },
    selectedFilesSize() {
      return this.files.reduce((ac, file) => {
        if (!file.errMsg) {
          ac += file.size
        }
        return ac
      }, 0)
    },
    availableSizeLimit() {
      return MAX_FILE_SIZE - this.selectedFilesSize
    },
    filesSizeUnderLimit() {
      return this.selectedFilesSize <= MAX_FILE_SIZE
    },
    disableUpload() {
      return this.multiple ? false : this.files.length === 1
    }
  },

  methods: {
    enableEdit() {
      const hasAlreadyUploadedDoc =
        !this.files.length && this.uploadedFile.file_url

      if (hasAlreadyUploadedDoc) {
        this.openFileSelect()
        return
      }
      this.editable = true
    },
    removeFile(i) {
      this.files && this.files.splice(i, 1)
    },
    openFileSelect() {
      this.$refs.fileSelect.click()
    },
    fileHasErr(file) {
      return file.errMsg
    },
    isValidType(fileType) {
      return this.multiple
        ? fileType === 'application/pdf'
        : ['application/pdf', 'image'].find((t) => fileType.startsWith(t))
    },
    validateFile(file) {
      const validationArr = [
        // type check
        (f) => {
          return this.isValidType(f.type)
            ? f.errMsg
            : this.multiple
            ? this.$t('invoices.add.helpers.pdfTypeOnly')
            : this.$t('invoices.add.helpers.pdfAndImagesOnly')
        },
        // if size limit is already reached
        (f) => {
          return this.filesSizeUnderLimit
            ? f.errMsg
            : this.$t('invoices.add.helpers.fileSizeError')
        },
        // if remaining size limit is less than selected file size
        (f) => {
          return f.size < this.availableSizeLimit
            ? f.errMsg
            : `${this.$t(
                'invoices.add.helpers.fileSizeAvailableError'
              )} ${formatBytes(this.availableSizeLimit)}`
        }
      ]

      for (let i = 0; i < validationArr.length; i++) {
        if (file.errMsg) {
          return file
        }
        file.errMsg = validationArr[i](file)
      }
      return file
    },
    addToFiles(files) {
      if (files.length) {
        const fArr = Array.from(files).map((file) => this.validateFile(file))
        this.editable = true
        this.files.unshift(...fArr)
        this.resetFileInput()
      }
    },
    onFileSelect(e) {
      this.addToFiles(e.target.files)
    },
    handleDrop(e) {
      if (e) {
        e.preventDefault()
        this.drag = false
        if (!this.disableUpload) {
          this.addToFiles(e.dataTransfer.files)
        }
      }
    },
    resetFileInput() {
      this.$refs.fileSelect.value = ''
    },
    async onSubmit() {
      if (this.submitDisabled) {
        return
      }
      try {
        this.loading = true

        const file = this.multiple ? this.files : this.files[0]
        const fd = new FormData()

        this.$mpTrack('Sending File', {
          file,
          task: this.task
        })

        fd.append('task_flow[tags]', this.uploadDoc.key)
        fd.append('task_flow[file_id]', this.uploadDoc.key)
        if (this.task.uid === 'upload_po') {
          fd.append('task_flow[po_file_multiple]', 1)
        }
        this.multiple
          ? file.forEach((f) => {
              fd.append('task_flow[file][]', f)
            })
          : fd.append('task_flow[file]', file)

        await uploadService.uploadFile(
          this.task.id,
          this.$store.getters.token,
          fd
        )

        this.editable = false

        this.$emit('success')

        this.$mpTrack('Uploaded File', {
          file,
          task: this.task
        })
      } catch (err) {
        Sentry.captureException(err)
      }
      this.loading = false
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../../../assets/scss/variables';

.file-upload-container {
  .uploaded-container {
    overflow: hidden;
    box-shadow: 0 0.125rem 0.3125rem 0 rgba(0, 0, 0, 0.05);
    border: solid 0.0625rem $drip-border-color;
    background-color: #fff;
    transition: all 0.2s;
    min-height: 6.625rem;
    border-radius: 0.3125rem;

    &:hover {
      box-shadow: 0 0.3125rem 0.8125rem 0 rgba(0, 0, 0, 0.14);
      border: solid 0.0625rem rgba(224, 231, 255, 0.5);
    }
  }
  .uploaded {
    display: flex;
    padding: 1.5rem 1.125rem;

    .success {
      width: 2rem;
      min-width: 2rem;
      height: 2rem;
      min-height: 2rem;
      margin-right: 0.75rem;
    }

    .content {
      flex: 1;
      .upload-placeholder-title {
        font-family: $montserrat;
        font-size: 1rem;
        font-weight: 500;
        color: $secondary;
        margin: 0;
        margin-bottom: 0.375rem;
      }

      .file-names-container {
        display: flex;
        flex-flow: wrap;

        .file-name {
          font-family: $lato;
          font-size: 0.625rem;
          line-height: 1.5;
          color: $dc-label-color;
          margin: 0 0.625rem 0.125rem 0;
        }
      }
    }

    .edit {
      cursor: pointer;
      background: $light-background-color;
      min-height: 2.1875rem;
      min-width: 2.1875rem;
      height: 2.1875rem;
      width: 2.1875rem;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      color: $dc-svg;
      transition: all 0.2s;
    }
  }

  .files-list-container {
    background-color: white;

    .file-li {
      display: flex;
      align-items: flex-start;
      padding: 0.5rem 0;
      border-top: solid 0.0625rem $dc-form-border-color;

      .icon-container {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 2.1875rem;
        min-width: 2.1875rem;
        height: 2.1875rem;
        width: 2.1875rem;
        margin: 0 0.875rem;

        button {
          cursor: pointer;
          background-color: transparent;
          border: none;
        }

        .cross {
          width: 0.75rem;
          height: 0.75rem;
          opacity: 0.4;
        }

        .success {
          width: 1.25rem;
          height: 1.25rem;
        }
      }

      .content {
        flex: 1;
        margin: 0.375rem 0 0;

        p {
          font-family: $lato;
          font-size: 0.75rem;
          line-height: 1.5;
          color: $dc-label-color;
          margin: 0;
          word-break: break-word;
        }

        .err-msg {
          font-size: 0.625rem;
          margin-top: 0.125rem;
        }
      }
    }
  }

  .uploader {
    .file-select-container {
      overflow: hidden;
      box-shadow: 0 0.125rem 0.3125rem 0 rgba(0, 0, 0, 0.05);
      border: solid 0.0625rem $drip-border-color;
      background-color: $drip-bg;
      transition: all 0.2s;
      min-height: 6.625rem;
      border-radius: 0.3125rem;

      .file-select {
        cursor: pointer;
        display: flex;
        align-items: center;
        height: 100%;
        width: 100%;
        position: relative;
        padding: 0.5rem 0;
        border-radius: 0.3125rem;
        min-height: 6.625rem;

        .icon-container {
          background: $light-background-color;
          min-height: 2.1875rem;
          min-width: 2.1875rem;
          height: 2.1875rem;
          width: 2.1875rem;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          color: $dc-svg;
          margin: 0 0.875rem;
          transition: all 0.2s;

          .file,
          .attachment {
            font-size: 1.5rem !important;

            &.file {
              bottom: 0.1875rem;
            }

            &.attachment {
              right: 0.1875rem;
              transform: rotate(270deg);
            }
          }
        }

        .content {
          flex: 1;

          .upload-placeholder-title {
            font-family: $montserrat;
            font-size: 1rem;
            font-weight: 500;
            color: $secondary;
            margin: 0;
          }

          .upload-desc,
          .upload-size {
            font-family: $lato;
            font-size: 0.625rem;
            line-height: 1.5;
            color: $dc-label-color;
            margin: 0;
          }
        }

        .uploader-hover-container {
          pointer-events: none;
          position: absolute;
          background-color: white;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;

          .uploader-hover {
            width: 100%;
            height: 100%;
            border: 0.125rem dashed $dc-upload;
            border-radius: 0.3125rem;
            background-color: rgba(46, 91, 255, 0.05);
            padding: 1rem 0;
            font-family: $montserrat;
            font-size: 1.25rem;
            font-weight: bold;
            text-align: center;
            color: rgba(53, 96, 255, 0.5);

            &.cannot-upload {
              border: 0.125rem dashed red;
              color: rgba(255, 53, 53, 0.5);
              background-color: rgba(255, 46, 46, 0.05);
            }
          }
        }

        &:hover {
          .icon-container {
            background: $secondary;
            color: $white;
          }
        }
      }

      &:hover {
        box-shadow: 0 0.3125rem 0.8125rem 0 rgba(0, 0, 0, 0.14);
        border: solid 0.0625rem rgba(224, 231, 255, 0.5);
      }

      .submit-button-container {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        padding: 0.5rem 0.875rem;
        border-top: solid 0.0625rem $dc-form-border-color;

        .desc {
          margin: 0 0.875rem 0 0;
          font-family: $lato;
          font-size: 0.625rem;
          line-height: 1.5;
          color: $dc-label-color;
        }

        .submit-button {
          min-width: 6rem;
          white-space: nowrap;
          font-family: $lato;
          font-size: 0.75rem;
          line-height: 1.5;
          color: white;
          background-color: $primary;
          border: none;
          padding: 0.375rem 0.875rem;
          cursor: pointer;
          border-radius: 0.3125rem;
          text-align: center;

          &.disabled {
            background-color: $dc-label-color;
          }
        }
      }
    }
  }
}
</style>
